import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

export const cancelInviteErrorMessages = defineMessages({
  inviteGone: {
    id: 'cancelInviteErrors.inviteGone',
    defaultMessage: 'This invite no longer exists.',
    description:
      'Toast shown when cancelling an invite fails because the backend reports it as already gone (no pending invite found, or invite not found)',
  },
  inviteExpired: {
    id: 'cancelInviteErrors.inviteExpired',
    defaultMessage: 'This invite has already expired.',
    description:
      'Toast shown when cancelling an invite fails because the backend reports the invite has expired',
  },
  permissionDenied: {
    id: 'cancelInviteErrors.permissionDenied',
    defaultMessage: "You don't have permission to cancel this invite.",
    description:
      'Toast shown when cancelling an invite fails because the backend reports the caller lacks permission for that legal entity',
  },
  inviteNotPending: {
    id: 'cancelInviteErrors.inviteNotPending',
    defaultMessage: 'This invite has already been accepted or resolved.',
    description:
      'Toast shown when cancelling an invite fails because the backend reports the invite is not in pending status',
  },
  genericError: {
    id: 'cancelInviteErrors.genericError',
    defaultMessage: 'Failed to cancel invite. Please try again.',
    description:
      'Fallback toast shown when cancelling an invite fails with an unrecognized or internal backend error',
  },
});

// Must stay in sync with the error strings BulkCancelInvites/cancelSingleInvite
// return in CancelInviteResult.error (account-management: internal/operations/bulk_cancel_invites.go).
// There's no error-code field to key off instead, so an unrecognized string
// silently falls back to genericError below rather than failing loudly.
const RAW_ERROR_TO_MESSAGE: ReadonlyMap<string, MessageDescriptor> = new Map([
  ['no pending invite found for this legal entity', cancelInviteErrorMessages.inviteGone],
  ['invite not found', cancelInviteErrorMessages.inviteGone],
  ['invite has expired', cancelInviteErrorMessages.inviteExpired],
  ['permission denied for this legal entity', cancelInviteErrorMessages.permissionDenied],
  ['invite is not in pending status', cancelInviteErrorMessages.inviteNotPending],
]);

export function getCancelInviteErrorMessage(rawError: string | undefined): MessageDescriptor {
  if (!rawError) {
    return cancelInviteErrorMessages.genericError;
  }

  return RAW_ERROR_TO_MESSAGE.get(rawError) ?? cancelInviteErrorMessages.genericError;
}
